import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@/store';
import { getSellerOrders, updateOrderStatus } from '@/store/slices/ordersSlice';
import { systemSettingsApi, ordersApi } from '@/services/api';
import { Order } from '@/types';
import { formatCurrency } from '@/utils/formatters';
import Button from '@/components/ui/Button';
import CommissionPaymentModal from '@/components/CommissionPaymentModal';
import {
  EyeIcon,
  TruckIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  BellIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const SellerOrdersPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orders, isLoading, error } = useSelector((state: RootState) => state.orders);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [hasError, setHasError] = useState(false);
  const [isFreeMode, setIsFreeMode] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState<Order | null>(null);

  useEffect(() => {
    try {
      dispatch(getSellerOrders());
    } catch (err) {
      console.error('Error dispatching getSellerOrders:', err);
      setHasError(true);
    }
    // Check free mode status
    systemSettingsApi.getFreeMode()
      .then(response => {
        setIsFreeMode(response.data.is_free_mode || false);
      })
      .catch(() => {
        setIsFreeMode(false);
      });
  }, [dispatch]);

  const filteredOrders = (orders || []).filter(order => {
    const matchesSearch = order.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ?? true;
    const matchesStatus = filterStatus === 'all' || order.order_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleUpdateStatus = (orderId: number, newStatus: string) => {
    if (window.confirm(`Are you sure you want to update this order to ${newStatus}?`)) {
      dispatch(updateOrderStatus({ id: orderId, newStatus }));
    }
  };

  const handlePayCommission = (order: Order) => {
    setSelectedOrderForPayment(order);
  };

  const handlePaymentSuccess = () => {
    dispatch(getSellerOrders());
    setSelectedOrderForPayment(null);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'shipped':
        return <TruckIcon className="h-5 w-5 text-blue-500" />;
      case 'confirmed':
        return <CheckCircleIcon className="h-5 w-5 text-blue-500" />;
      case 'cancelled':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getNextStatus = (currentStatus: string) => {
    switch (currentStatus) {
      case 'pending':
        return 'confirmed';
      case 'confirmed':
        return 'shipped';
      case 'shipped':
        return 'delivered';
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  if ((error || hasError) && (!orders || orders.length === 0)) {
    // Safely convert error to string
    const errorMessage = typeof error === 'string' ? error : error?.toString() || 'An error occurred';
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-500 mb-4">
            <ExclamationTriangleIcon className="h-16 w-16 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Orders</h2>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => dispatch(getSellerOrders())}>
              Retry
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard/seller')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Order Management</h1>
              <p className="mt-2 text-gray-600">Manage your orders and fulfillments</p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {typeof error === 'string' ? error : error?.toString() || 'An error occurred'}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Results Count */}
            <div className="flex items-center text-sm text-gray-600">
              <FunnelIcon className="h-4 w-4 mr-2" />
              {filteredOrders.length} orders found
            </div>
          </div>
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
            <p className="text-gray-600">
              {searchQuery || filterStatus !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'You haven\'t received any orders yet.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commission
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {order.order_number}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(order.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          Customer #{order.buyer_id}
                        </div>
                        <div className="text-sm text-gray-500">
                          {(!isFreeMode && order.requires_commission_payment && !order.commission_paid) 
                            ? 'Hidden until commission is paid'
                            : order.shipping_address}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(order.order_status)}
                          <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.order_status)}`}>
                            {order.order_status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(Number(order.total_amount || 0))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {order.commission_paid ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              Paid
                            </span>
                          ) : order.requires_commission_payment ? (
                            <div>
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 block mb-1">
                                Required
                              </span>
                              <span className="text-xs text-gray-600">
                                {formatCurrency(Number(order.platform_commission || 0))}
                              </span>
                            </div>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                              Not Required
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/dashboard/seller/orders/${order.id}`)}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                          
                          {getNextStatus(order.order_status) && (
                            <Button
                              size="sm"
                              onClick={() => handleUpdateStatus(order.id, getNextStatus(order.order_status)!)}
                            >
                              {getNextStatus(order.order_status) === 'confirmed' && 'Confirm'}
                              {getNextStatus(order.order_status) === 'shipped' && 'Ship'}
                              {getNextStatus(order.order_status) === 'delivered' && 'Deliver'}
                            </Button>
                          )}
                          
                          {order.requires_commission_payment && !order.commission_paid && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handlePayCommission(order)}
                              className="relative"
                            >
                              <BellIcon className="h-4 w-4 mr-1" />
                              Pay Commission
                              {order.order_status === 'pending' && (
                                <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full"></span>
                              )}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Commission Payment Modal */}
      {selectedOrderForPayment && (
        <CommissionPaymentModal
          isOpen={!!selectedOrderForPayment}
          onClose={() => setSelectedOrderForPayment(null)}
          order={{
            id: selectedOrderForPayment.id,
            order_number: selectedOrderForPayment.order_number || '',
            total_amount: Number(selectedOrderForPayment.total_amount || 0),
            platform_commission: Number(selectedOrderForPayment.platform_commission || 0),
            requires_commission_payment: selectedOrderForPayment.requires_commission_payment,
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default SellerOrdersPage;